export type CoverImageStyle = {
  width: string
  height: string
  position: 'absolute'
  left: string
  top: string
}

export type FaceBoundingBox = {
  left: string
  top: string
  width: string
  height: string
}

/**
 * Calculates image style and face box positions for object-fit: cover behavior
 * @param imageWidth - Full image width
 * @param imageHeight - Full image height
 * @param containerWidth - Container width in pixels
 * @param containerHeight - Container height in pixels
 * @returns Image style and a function to calculate face box positions
 */
export function calculateCoverLayout(
  imageWidth: number,
  imageHeight: number,
  containerWidth: number,
  containerHeight: number,
) {
  const imageAspect = imageWidth / imageHeight
  const containerAspect = containerWidth / containerHeight

  let displayWidth: number
  let displayHeight: number

  // Cover: scale so the smaller dimension fills
  if (imageAspect > containerAspect) {
    // Image is wider - match height, width overflows
    displayHeight = containerHeight
    displayWidth = displayHeight * imageAspect
  } else {
    // Image is taller - match width, height overflows
    displayWidth = containerWidth
    displayHeight = displayWidth / imageAspect
  }

  // Center the image
  const left = (containerWidth - displayWidth) / 2
  const top = (containerHeight - displayHeight) / 2

  const imageStyle: CoverImageStyle = {
    width: `${displayWidth}px`,
    height: `${displayHeight}px`,
    position: 'absolute',
    left: `${left}px`,
    top: `${top}px`,
  }

  // Function to scale face boxes to the displayed image
  const scaleFaceBox = (boundingBox: [number, number, number, number]): FaceBoundingBox => {
    const scale = displayWidth / imageWidth
    const scaledX = boundingBox[0] * scale
    const scaledY = boundingBox[1] * scale
    const scaledWidth = boundingBox[2] * scale
    const scaledHeight = boundingBox[3] * scale

    return {
      left: `${left + scaledX}px`,
      top: `${top + scaledY}px`,
      width: `${scaledWidth}px`,
      height: `${scaledHeight}px`,
    }
  }

  return { imageStyle, scaleFaceBox }
}
